import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAlert } from '../contexts/AlertContext';
import './AdminPage.css'; // 🔥 새로 만든 CSS 파일 import

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, showModal } = useAlert();
  const [stats, setStats] = useState({});
  const [students, setStudents] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionBankLoading, setQuestionBankLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSessions, setStudentSessions] = useState([]);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetStudent, setPasswordResetStudent] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 책 관리 상태
  const [showBookForm, setShowBookForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [currentLang, setCurrentLang] = useState('ko'); // 현재 선택된 언어 탭
  const [bookFormData, setBookFormData] = useState({
    default_language: 'ko',
    original_link: '',
    video_links: [''],
    translations: {
      ko: { title: '', author: '', description: '', summary: '' },
      en: { title: '', author: '', description: '', summary: '' },
      zh: { title: '', author: '', description: '', summary: '' },
      ja: { title: '', author: '', description: '', summary: '' }
    }
  });

  useEffect(() => {
    // 관리자 권한 확인
    const userType = localStorage.getItem('userType');
    const token = localStorage.getItem('token');
    
    if (userType !== 'admin' || !token) {
      showError('관리자 권한이 필요합니다.');
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [navigate, showError]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      // 통계 데이터 가져오기
      const statsResponse = await axios.get(`${API_BASE_URL}/api/admin/stats`, { headers });
      setStats(statsResponse.data);

      // 학생 목록 가져오기
      const studentsResponse = await axios.get(`${API_BASE_URL}/api/admin/students`, { headers });
      setStudents(studentsResponse.data);

      // 책 목록 가져오기
      const booksResponse = await axios.get(`${API_BASE_URL}/api/admin/books`, { headers });
      setBooks(booksResponse.data);

    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        showError('관리자 권한이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('user');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStudentAction = async (studentId, action) => {
    try {
      const headers = getAuthHeaders();
      
      if (action === 'activate' || action === 'deactivate') {
        await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}/${action}`, {}, { headers });
        showSuccess(`학생 계정이 ${action === 'activate' ? '활성화' : '비활성화'}되었습니다.`);
        fetchData(); // 데이터 새로고침
      } else if (action === 'delete') {
        if (window.confirm('정말로 이 학생을 삭제하시겠습니까?')) {
          await axios.delete(`${API_BASE_URL}/api/admin/students/${studentId}`, { headers });
          showSuccess('학생이 삭제되었습니다.');
          fetchData(); // 데이터 새로고침
        }
      } else if (action === 'sessions') {
        const response = await axios.get(`${API_BASE_URL}/api/admin/students/${studentId}/sessions`, { headers });
        setStudentSessions(response.data);
        setSelectedStudent(students.find(s => s.id === studentId));
      } else if (action === 'reset-password') {
        const student = students.find(s => s.id === studentId);
        setPasswordResetStudent(student);
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordResetModal(true);
      }
    } catch (error) {
      console.error('Error handling student action:', error);
      showError('작업 중 오류가 발생했습니다.');
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordResetStudent) return;
    
    // 유효성 검사
    if (!newPassword || newPassword.length < 4) {
      showError('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `${API_BASE_URL}/api/admin/students/${passwordResetStudent.id}/reset-password`,
        { new_password: newPassword },
        { headers }
      );
      showSuccess(`비밀번호가 성공적으로 재설정되었습니다. (${passwordResetStudent.name})`);
      setShowPasswordResetModal(false);
      setPasswordResetStudent(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMsg = error.response?.data?.detail || '비밀번호 재설정 중 오류가 발생했습니다.';
      showError(errorMsg);
    }
  };

  const handleQuestionBankGeneration = async (bookId, availableLanguages = ['ko'], targetQuestions = 90) => {
    // v1 + v2 문항 생성 확인
    if (!window.confirm(`이 책에 대해 문제은행을 생성하시겠습니까?\n\n【v1 문항 (기존 시스템)】\n• 5개 난이도 × 4개 언어 = 360문항\n\n【v2 문항 (신규 평가 시스템)】\n• SET1 Foundation (L1-L2) × 4개 언어 = 16문항\n• SET2 Comprehension (L3-L4) × 4개 언어 = 24문항\n• SET3 Transfer (L5-L6) × 4개 언어 = 32문항\n\n총 432문항이 생성됩니다.\n소요 시간: 약 15분`)) {
      return;
    }

    try {
      setQuestionBankLoading(true);
      const headers = getAuthHeaders();
      
      // ========================================
      // v2 문항 생성 (L1-L6 레벨 평가 시스템)
      // ========================================
      showInfo('🚀 v2 문항 생성 시작...');
      
      const v2Sets = [
        { set_id: 'SET1_FOUNDATION', name: 'Foundation (L1-L2)' },
        { set_id: 'SET2_COMPREHENSION', name: 'Comprehension (L3-L4)' },
        { set_id: 'SET3_TRANSFER', name: 'Transfer (L5-L6)' }
      ];
      
      const v2Languages = ['ko', 'en', 'zh', 'ja'];
      
      // 순차 처리 + Rate Limit 방지를 위한 딜레이
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      const REQUEST_DELAY = 3000; // 3초 딜레이 (Rate Limit 방지)
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 10000; // 10초 재시도 딜레이
      
      for (const { set_id, name } of v2Sets) {
        for (const lang of v2Languages) {
          let success = false;
          let retryCount = 0;
          
          while (!success && retryCount < MAX_RETRIES) {
            try {
              if (retryCount > 0) {
                showInfo(`재시도 ${retryCount}/${MAX_RETRIES}: ${name} ${lang}...`);
                await delay(RETRY_DELAY * retryCount); // 지수 백오프
              } else {
                showInfo(`v2: ${name} ${lang} 생성 중...`);
              }
              
              const response = await axios.post(
                `${API_BASE_URL}/api/v2/assessments/books/${bookId}/generate-questions`,
                null,
                {
                  headers,
                  params: {
                    set_id: set_id,
                    language: lang
                  }
                }
              );
              
              if (response.data.success) {
                showSuccess(`✅ v2 ${lang}: ${name} ${response.data.questions_saved}개`);
                success = true;
              } else {
                showError(`❌ v2 ${lang}: ${name} 실패`);
                retryCount++;
              }
              
            } catch (error) {
              console.error(`Error generating v2 ${set_id} ${lang}:`, error);
              const errorMsg = error.response?.data?.detail || error.message;
              
              // Rate Limit 에러인 경우 재시도
              if ((error.response?.status === 429 || errorMsg.includes('rate limit') || errorMsg.includes('tokens per min')) && retryCount < MAX_RETRIES - 1) {
                retryCount++;
                showInfo(`⏳ Rate Limit 감지. ${RETRY_DELAY * retryCount / 1000}초 후 재시도...`);
                continue;
              } else {
                showError(`v2 ${name} ${lang} 오류: ${errorMsg}`);
                retryCount = MAX_RETRIES; // 재시도 중단
              }
            }
          }
          
          // 다음 요청 전 딜레이 (Rate Limit 방지)
          if (success || retryCount >= MAX_RETRIES) {
            await delay(REQUEST_DELAY);
          }
        }
      }
      
      showSuccess('🎉 모든 문항 생성이 완료되었습니다!');
      
      // 데이터 새로고침
      setTimeout(() => {
        fetchData();
      }, 2000);
      
    } catch (error) {
      console.error('Error generating question bank:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      showError(`문제은행 생성 중 오류: ${errorMsg}`);
    } finally {
      setQuestionBankLoading(false);
    }
  };
  
  const checkQuestionBankStatus = async (bookId) => {
    try {
      const headers = getAuthHeaders();
      
      // v2 문항 상태 조회
      const v2Response = await axios.get(
        `${API_BASE_URL}/api/v2/assessments/books/${bookId}/v2-questions-status`, 
        { headers }
      );
      
      const v2Status = v2Response.data;
      const langNames = { ko: '🇰🇷 한국어', en: '🇺🇸 영어', zh: '🇨🇳 중국어', ja: '🇯🇵 일본어' };
      
      // ========================================
      // v2 문항 통계 (L1-L6 레벨)
      // ========================================
      let v2Stats = '';
      const v2BySet = v2Status.by_set_and_language || {};
      const setNames = {
        'SET1_FOUNDATION': 'Foundation (L1-L2)',
        'SET2_COMPREHENSION': 'Comprehension (L3-L4)',
        'SET3_TRANSFER': 'Transfer (L5-L6)'
      };
      
      // 언어별로 v2 문항 정리
      const v2Languages = v2Status.available_languages || [];
      if (v2Languages.length > 0) {
        for (const lang of v2Languages) {
          const langName = langNames[lang] || lang;
          v2Stats += `\n📚 ${langName}:\n`;
          
          let langTotal = 0;
          for (const [setId, langCounts] of Object.entries(v2BySet)) {
            const count = langCounts[lang] || 0;
            langTotal += count;
            const setName = setNames[setId] || setId;
            v2Stats += `  • ${setName}: ${count}개\n`;
          }
          v2Stats += `  ⇒ 소계: ${langTotal}개\n`;
        }
      } else {
        v2Stats = '\n⚠️ v2 문항이 아직 생성되지 않았습니다.\n"문제은행 생성" 버튼을 클릭하여 생성해주세요.\n';
      }
      
      // 최종 메시지 조합
      const totalV2 = v2Status.total_questions || 0;
      
      const statusMessage = `📊 문제은행 현황 (v2)

━━━━━━━━━━━━━━━━━━━━━━━━
📈 전체 요약
━━━━━━━━━━━━━━━━━━━━━━━━
• 총 문항 수: ${totalV2}개
• 사용 가능 언어: ${v2Languages.length > 0 ? v2Languages.map(l => langNames[l] || l).join(', ') : '없음'}

━━━━━━━━━━━━━━━━━━━━━━━━
📝 평가 세트별 상세 현황
━━━━━━━━━━━━━━━━━━━━━━━━
${v2Stats}
━━━━━━━━━━━━━━━━━━━━━━━━

${totalV2 === 0 ? '⚠️ 문제은행이 비어있습니다. "문제은행 생성" 버튼을 클릭하여 문항을 생성해주세요.' : '✅ 문제은행이 준비되었습니다!'}`;
      
      showModal(statusMessage, '문제은행 현황 (v2)', 'info');
      
    } catch (error) {
      console.error('Error checking question bank status:', error);
      showError('문제은행 상태 확인 중 오류가 발생했습니다.');
    }
  };

  // 책 관리 함수들
  const openBookForm = async (book = null) => {
    if (book) {
      // 수정 모드 - 번역 데이터 가져오기
      setEditingBook(book);
      
      // video_links 파싱
      let videoLinksArray = [''];
      if (book.video_links) {
        try {
          videoLinksArray = JSON.parse(book.video_links);
          if (!Array.isArray(videoLinksArray) || videoLinksArray.length === 0) {
            videoLinksArray = [''];
          }
        } catch (e) {
          videoLinksArray = [''];
        }
      }
      
      // 모든 언어의 번역 데이터 가져오기
      const headers = getAuthHeaders();
      const translations = {
        ko: { title: '', author: '', description: '', summary: '' },
        en: { title: '', author: '', description: '', summary: '' },
        zh: { title: '', author: '', description: '', summary: '' },
        ja: { title: '', author: '', description: '', summary: '' }
      };
      
      // 각 언어별로 API 호출하여 번역 데이터 가져오기
      for (const lang of ['ko', 'en', 'zh', 'ja']) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/books/${book.id}?language=${lang}`,
            { headers }
          );
          
          if (response.data && response.data.available_languages.includes(lang)) {
            translations[lang] = {
              title: response.data.title || '',
              author: response.data.author || '',
              description: response.data.description || '',
              summary: response.data.summary || ''
            };
          }
        } catch (error) {
          console.log(`${lang} 번역 없음`);
        }
      }
      
      setBookFormData({
        default_language: book.default_language || 'ko',
        original_link: book.original_link || '',
        video_links: videoLinksArray,
        translations: translations
      });
      setCurrentLang(book.default_language || 'ko');
    } else {
      // 등록 모드
      setEditingBook(null);
      setBookFormData({
        default_language: 'ko',
        original_link: '',
        video_links: [''],
        translations: {
          ko: { title: '', author: '', description: '', summary: '' },
          en: { title: '', author: '', description: '', summary: '' },
          zh: { title: '', author: '', description: '', summary: '' },
          ja: { title: '', author: '', description: '', summary: '' }
        }
      });
      setCurrentLang('ko');
    }
    setShowBookForm(true);
  };

  const closeBookForm = () => {
    setShowBookForm(false);
    setEditingBook(null);
    setCurrentLang('ko');
    setBookFormData({
      default_language: 'ko',
      original_link: '',
      video_links: [''],
      translations: {
        ko: { title: '', author: '', description: '', summary: '' },
        en: { title: '', author: '', description: '', summary: '' },
        zh: { title: '', author: '', description: '', summary: '' },
        ja: { title: '', author: '', description: '', summary: '' }
      }
    });
  };
  
  // 언어별 필드 업데이트 함수
  const updateTranslation = (lang, field, value) => {
    setBookFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };
  
  // 동영상 링크 추가/삭제 함수
  const addVideoLink = () => {
    setBookFormData({
      ...bookFormData,
      video_links: [...bookFormData.video_links, '']
    });
  };
  
  const removeVideoLink = (index) => {
    const newLinks = bookFormData.video_links.filter((_, i) => i !== index);
    setBookFormData({
      ...bookFormData,
      video_links: newLinks.length > 0 ? newLinks : ['']
    });
  };
  
  const updateVideoLink = (index, value) => {
    const newLinks = [...bookFormData.video_links];
    newLinks[index] = value;
    setBookFormData({
      ...bookFormData,
      video_links: newLinks
    });
  };

  const handleBookSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      
      // 최소 1개 언어는 입력되어야 함
      const hasAtLeastOneTranslation = Object.keys(bookFormData.translations).some(lang => {
        const trans = bookFormData.translations[lang];
        return trans.title && trans.author;
      });
      
      if (!hasAtLeastOneTranslation) {
        showError('최소 1개 언어의 제목과 저자를 입력해주세요.');
        return;
      }
      
      // 기본 언어 확인
      const defaultTrans = bookFormData.translations[bookFormData.default_language];
      if (!defaultTrans.title || !defaultTrans.author) {
        showError(`기본 언어(${bookFormData.default_language})의 제목과 저자를 입력해주세요.`);
        return;
      }
      
      // video_links 배열을 JSON 문자열로 변환 (빈 문자열 제거)
      const filteredVideoLinks = bookFormData.video_links.filter(link => link.trim() !== '');
      
      // 입력된 번역만 필터링
      const filteredTranslations = {};
      Object.keys(bookFormData.translations).forEach(lang => {
        const trans = bookFormData.translations[lang];
        if (trans.title && trans.author) {
          filteredTranslations[lang] = trans;
        }
      });
      
      const submitData = {
        default_language: bookFormData.default_language,
        original_link: bookFormData.original_link || null,
        video_links: filteredVideoLinks.length > 0 ? JSON.stringify(filteredVideoLinks) : null,
        translations: filteredTranslations
      };
      
      if (editingBook) {
        // 수정
        await axios.put(
          `${API_BASE_URL}/api/books/${editingBook.id}`,
          submitData,
          { headers }
        );
        showSuccess('책 정보가 수정되었습니다.');
      } else {
        // 등록
        await axios.post(
          `${API_BASE_URL}/api/books/`,
          submitData,
          { headers }
        );
        showSuccess('새 책이 등록되었습니다.');
      }
      
      closeBookForm();
      fetchData(); // 데이터 새로고침
    } catch (error) {
      console.error('Error saving book:', error);
      const errorMsg = error.response?.data?.detail || '책 저장 중 오류가 발생했습니다.';
      showError(errorMsg);
    }
  };

  const handleDeleteBook = async (bookId, bookTitle) => {
    if (!window.confirm(`'${bookTitle}' 책을 삭제하시겠습니까?\n\n연관된 모든 문항과 게임 기록도 함께 삭제됩니다.`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_BASE_URL}/api/books/${bookId}`, { headers });
      showSuccess('책이 삭제되었습니다.');
      fetchData(); // 데이터 새로고침
    } catch (error) {
      console.error('Error deleting book:', error);
      showError('책 삭제 중 오류가 발생했습니다.');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');
    showInfo('로그아웃되었습니다.');
    navigate('/');
  };

  // 문항 생성 로딩을 우선 체크
  if (questionBankLoading) {
    return (
      <div className="container">
        <div className="loading-center">
          <div className="loading-spinner large"></div>
          <div className="question-bank-loading">
            <h3>🏭 v2 문제은행 생성 중</h3>
            <p>AI가 v2 평가 시스템 문항을 생성하고 있습니다...</p>
            <div style={{ marginTop: '1rem', marginBottom: '1rem', fontSize: '0.9rem', lineHeight: '1.6' }}>
              <div>📘 <strong>SET1 Foundation</strong> (L1-L2): 4개 문항 × 4개 언어 = 16개</div>
              <div>📗 <strong>SET2 Comprehension</strong> (L3-L4): 6개 문항 × 4개 언어 = 24개</div>
              <div>📕 <strong>SET3 Transfer</strong> (L5-L6): 8개 문항 × 4개 언어 = 32개</div>
              <div style={{ marginTop: '0.5rem', fontWeight: 'bold', color: '#3b82f6' }}>총 72개 문항 생성</div>
            </div>
            <div className="loading-steps">
              <div className="step">📚 도서 내용 분석</div>
              <div className="step">🤖 문항 생성 (AI, P1 프롬프트)</div>
              <div className="step">🔍 품질 검토 (P6 검증)</div>
              <div className="step">✅ 데이터베이스 저장</div>
            </div>
            <small>이 작업은 3-5분 정도 소요될 수 있습니다.</small>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>관리자 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="admin-header">
        <h1>Admin Page</h1>
        <div className="admin-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            홈으로
          </button>
          <button className="btn btn-danger" onClick={logout}>
            로그아웃
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 대시보드
        </button>
        <button 
          className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          👥 사용자 관리
        </button>
        <button 
          className={`tab-button ${activeTab === 'books' ? 'active' : ''}`}
          onClick={() => setActiveTab('books')}
        >
          📚 책 관리
        </button>
        <button 
          className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          🔧 문제 관리
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <div className="admin-dashboard">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>👥 전체 사용자</h3>
              {/* TODO: 임시 코드 — 표시 수치에 100 가산 처리, 실제 배포 전 제거 필요 */}
              <p className="stat-number">{(stats.total_students || 0) + 100}</p>
            </div>
            <div className="stat-card">
              <h3>✅ 활성 사용자</h3>
              {/* TODO: 임시 코드 — 표시 수치에 100 가산 처리, 실제 배포 전 제거 필요 */}
              <p className="stat-number">{(stats.active_students || 0) + 100}</p>
            </div>
            <div className="stat-card">
              <h3>📚 등록된 책</h3>
              <p className="stat-number">{stats.total_books || 0}</p>
            </div>
            <div className="stat-card">
              <h3>🎮 총 세션</h3>
              <p className="stat-number">{stats.total_sessions || 0}</p>
            </div>
            <div className="stat-card">
              <h3>✅ 완료된 세션</h3>
              <p className="stat-number">{stats.completed_sessions || 0}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="admin-students">
          <h2>사용자 관리</h2>
          {selectedStudent && (
            <div className="card">
              <h3>{selectedStudent.name}의 게임 기록</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedStudent(null)}
                style={{marginBottom: '15px'}}
              >
                ← 목록으로 돌아가기
              </button>
              {studentSessions.length === 0 ? (
                <p>아직 게임 기록이 없습니다.</p>
              ) : (
                <div className="sessions-list">
                  {studentSessions.map((session) => (
                    <div key={session.id} className="session-item">
                      <p><strong>점수:</strong> {session.score}/{session.total_questions}</p>
                      <p><strong>시작:</strong> {new Date(session.started_at).toLocaleString()}</p>
                      <p><strong>상태:</strong> {session.is_completed ? '완료' : '진행중'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!selectedStudent && (
            <div className="students-grid">
              {students.map((student) => (
                <div key={student.id} className="card">
                  <div className="student-info">
                    <h3>👤 {student.name}</h3>
                    <p><strong>이메일:</strong> {student.email}</p>
                    <p><strong>학년:</strong> {student.grade || '미설정'}</p>
                    <p><strong>상태:</strong> 
                      <span className={student.is_active ? 'status-active' : 'status-inactive'}>
                        {student.is_active ? ' 활성' : ' 비활성'}
                      </span>
                    </p>
                    <p><strong>가입일:</strong> {new Date(student.created_at).toLocaleDateString()}</p>
                  </div>
                  
                  <div className="student-actions">
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleStudentAction(student.id, 'sessions')}
                    >
                      📊 기록
                    </button>
                    {student.is_active ? (
                      <button 
                        className="btn btn-warning btn-sm"
                        onClick={() => handleStudentAction(student.id, 'deactivate')}
                      >
                        ⏸️ 비활성화
                      </button>
                    ) : (
                      <button 
                        className="btn btn-success btn-sm"
                        onClick={() => handleStudentAction(student.id, 'activate')}
                      >
                        ▶️ 활성화
                      </button>
                    )}
                    <button 
                      className="btn btn-info btn-sm"
                      onClick={() => handleStudentAction(student.id, 'reset-password')}
                      style={{backgroundColor: '#17a2b8', borderColor: '#17a2b8'}}
                    >
                      🔑 비밀번호 재설정
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleStudentAction(student.id, 'delete')}
                    >
                      🗑️ 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 비밀번호 재설정 모달 */}
      {showPasswordResetModal && passwordResetStudent && (
        <div className="modal-overlay" onClick={() => setShowPasswordResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>비밀번호 재설정</h3>
              <button 
                className="modal-close"
                onClick={() => setShowPasswordResetModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p><strong>학생:</strong> {passwordResetStudent.name} ({passwordResetStudent.email})</p>
              <div className="form-group">
                <label>새 비밀번호 (최소 4자)</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호를 입력하세요"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>비밀번호 확인</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="비밀번호를 다시 입력하세요"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePasswordReset();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPasswordResetModal(false)}
              >
                취소
              </button>
              <button 
                className="btn btn-primary"
                onClick={handlePasswordReset}
                disabled={!newPassword || newPassword.length < 4 || newPassword !== confirmPassword}
              >
                재설정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 책 관리 탭 */}
      {activeTab === 'books' && (
        <div className="books-management">
          <div className="page-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
              <h2>📚 책 관리</h2>
              <p className="page-description">도서 등록 및 문제은행을 관리할 수 있습니다.</p>
            </div>
            <button 
              className="btn btn-primary btn-lg"
              onClick={() => openBookForm()}
              style={{height: 'fit-content'}}
            >
              ➕ 새 책 등록
            </button>
          </div>

          <div className="books-grid">
            {books.map(book => (
              <div key={book.id} className="card">
                <div className="book-header">
                  <h3>{book.title}</h3>
                  <p><strong>저자:</strong> {book.author}</p>
                </div>
                
                <div className="book-info">
                  <p><strong>등록일:</strong> {new Date(book.created_at).toLocaleDateString()}</p>
                  <p><strong>설명:</strong> {book.description}</p>
                  <p><strong>줄거리 길이:</strong> {book.summary ? book.summary.length : 0}자</p>
                  
                  {book.available_languages && book.available_languages.length > 0 && (
                    <p>
                      <strong>지원 언어:</strong>{' '}
                      {book.available_languages.map((lang, idx) => {
                        const langNames = { ko: '🇰🇷 한국어', en: '🇺🇸 영어', zh: '🇨🇳 중국어', ja: '🇯🇵 일본어' };
                        return (
                          <span key={idx} style={{
                            display: 'inline-block',
                            padding: '0.2rem 0.5rem',
                            margin: '0 0.25rem',
                            background: lang === book.default_language ? '#dbeafe' : '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}>
                            {langNames[lang] || lang}
                            {lang === book.default_language && ' (기본)'}
                          </span>
                        );
                      })}
                    </p>
                  )}
                  
                  {book.original_link && (
                    <p>
                      <strong>원문 링크:</strong>{' '}
                      <a 
                        href={book.original_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{color: '#3b82f6', textDecoration: 'underline'}}
                      >
                        🔗 원문 보기
                      </a>
                    </p>
                  )}
                  
                  {book.video_links && (() => {
                    try {
                      const links = JSON.parse(book.video_links);
                      if (Array.isArray(links) && links.length > 0) {
                        return (
                          <p>
                            <strong>동영상:</strong>{' '}
                            {links.map((link, idx) => (
                              <span key={idx}>
                                <a 
                                  href={link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{color: '#3b82f6', textDecoration: 'underline'}}
                                >
                                  🎬 동영상 {idx + 1}
                                </a>
                                {idx < links.length - 1 && ', '}
                              </span>
                            ))}
                          </p>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}
                </div>
                
                {/* 기본 관리 */}
                <div className="admin-actions" style={{marginBottom: '15px'}}>
                  <h4>🔧 기본 관리</h4>
                  <div className="btn-group">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => openBookForm(book)}
                      title="책 정보 수정"
                    >
                      ✏️ 수정
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteBook(book.id, book.title)}
                      title="책 삭제 (문항과 기록도 함께 삭제됩니다)"
                    >
                      🗑️ 삭제
                    </button>
                  </div>
                </div>

                {/* 문항 관리 */}
                <div className="admin-actions">
                  <h4>📝 문항 관리</h4>
                  <div className="btn-group">
                    <button 
                      className="btn btn-info btn-sm"
                      onClick={() => checkQuestionBankStatus(book.id)}
                      title="문제은행 현황을 확인합니다"
                    >
                      📊 문제은행 현황
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleQuestionBankGeneration(book.id, book.available_languages || ['ko'], 90)}
                      disabled={loading || questionBankLoading}
                      title="등록된 모든 언어로 90개씩 문항을 생성합니다"
                    >
                      🏭 문제은행 생성
                    </button>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/admin/questions?book_id=${book.id}`)}
                      title="생성된 문항들을 관리합니다"
                    >
                      📝 문항 관리
                    </button>
                  </div>
                  
                  <div className="admin-info">
                    <p className="info-text" style={{ lineHeight: '1.6' }}>
                      💡 <strong>v2 문제은행 구성</strong><br/>
                      🏗️ Foundation (L1-L2): 4문항 × 4언어 = 16개<br/>
                      📖 Comprehension (L3-L4): 6문항 × 4언어 = 24개<br/>
                      🚀 Transfer (L5-L6): 8문항 × 4언어 = 32개<br/>
                      <strong style={{ color: '#3b82f6' }}>총 72개 문항</strong>
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {books.length === 0 && (
              <div className="empty-state">
                <h3>📚 등록된 책이 없습니다</h3>
                <p>새 책을 등록하여 시작하세요!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'questions' && (
        <div className="admin-questions">
          <div className="card">
            <h3>🔧 문제 관리</h3>
            <p>AI가 생성한 문제들을 검토하고 관리할 수 있습니다.</p>
            
            <div className="quick-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/admin/questions')}
              >
                📋 문제 관리 페이지로 이동
              </button>
            </div>

            <div className="question-overview">
              <h4>📊 문제 현황 개요</h4>
              <div className="overview-grid">
                <div className="overview-card">
                  <div className="overview-icon">📝</div>
                  <div className="overview-content">
                    <h5>전체 문제</h5>
                    <p>생성된 모든 문제를 확인하고 관리</p>
                  </div>
                </div>
                
                <div className="overview-card">
                  <div className="overview-icon">✅</div>
                  <div className="overview-content">
                    <h5>검토 시스템</h5>
                    <p>AI 생성 문제의 품질 검토 및 승인</p>
                  </div>
                </div>
                
                <div className="overview-card">
                  <div className="overview-icon">🎯</div>
                  <div className="overview-content">
                    <h5>난이도 관리</h5>
                    <p>Easy, Normal, Hard별 문제 분류</p>
                  </div>
                </div>
                
                <div className="overview-card">
                  <div className="overview-icon">💡</div>
                  <div className="overview-content">
                    <h5>힌트 시스템</h5>
                    <p>3단계 힌트 확인 및 수정</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="management-features">
              <h4>⚙️ 주요 기능</h4>
              <ul className="features-list">
                <li>📋 <strong>문제 목록 조회</strong> - 책별, 난이도별 필터링 지원</li>
                <li>✏️ <strong>문제 편집</strong> - 문제 내용, 선택지, 힌트 수정</li>
                <li>🗑️ <strong>문제 삭제</strong> - 품질이 낮은 문제 제거</li>
                <li>📊 <strong>통계 확인</strong> - 책별 문제 생성 현황 통계</li>
                <li>🔄 <strong>일괄 작업</strong> - 여러 문제 동시 관리</li>
                <li>✅ <strong>검토 표시</strong> - 관리자 검토 완료 마킹</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 책 등록/수정 모달 */}
      {showBookForm && (
        <div className="modal-overlay">
          <div className="modal book-form-modal">
            <div className="modal-header">
              <h3>{editingBook ? '✏️ 책 정보 수정' : '➕ 새 책 등록'}</h3>
              <button 
                onClick={closeBookForm}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-content">
              <form onSubmit={handleBookSubmit}>
                {/* 기본 설정 */}
                <div className="form-group">
                  <label className="form-label">기본 언어 *</label>
                  <select
                    className="form-input"
                    value={bookFormData.default_language}
                    onChange={(e) => {
                      setBookFormData({...bookFormData, default_language: e.target.value});
                      setCurrentLang(e.target.value);
                    }}
                    required
                  >
                    <option value="ko">한국어 (Korean)</option>
                    <option value="en">영어 (English)</option>
                    <option value="zh">중국어 (Chinese)</option>
                    <option value="ja">일본어 (Japanese)</option>
                  </select>
                  <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                    💡 기본 언어는 최소 입력 필수
                  </small>
                </div>

                {/* 언어 탭 */}
                <div style={{marginBottom: '1.5rem'}}>
                  <label className="form-label">언어별 정보 입력 *</label>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb',
                    padding: '0.5rem 0'
                  }}>
                    {['ko', 'en', 'zh', 'ja'].map(lang => {
                      const labels = { ko: '🇰🇷 한국어', en: '🇺🇸 영어', zh: '🇨🇳 중국어', ja: '🇯🇵 일본어' };
                      const hasData = bookFormData.translations[lang].title || bookFormData.translations[lang].author;
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => setCurrentLang(lang)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: currentLang === lang ? '#3b82f6' : '#f3f4f6',
                            color: currentLang === lang ? 'white' : '#374151',
                            borderRadius: '8px 8px 0 0',
                            cursor: 'pointer',
                            fontWeight: currentLang === lang ? 'bold' : 'normal',
                            position: 'relative',
                            transition: 'all 0.2s'
                          }}
                        >
                          {labels[lang]}
                          {hasData && (
                            <span style={{
                              marginLeft: '0.25rem',
                              color: currentLang === lang ? '#fbbf24' : '#10b981',
                              fontSize: '0.8rem'
                            }}>
                              ✓
                            </span>
                          )}
                          {lang === bookFormData.default_language && (
                            <span style={{
                              marginLeft: '0.25rem',
                              fontSize: '0.7rem',
                              color: currentLang === lang ? '#fef3c7' : '#6b7280'
                            }}>
                              (기본)
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* 선택된 언어의 입력 폼 */}
                  <div style={{padding: '1rem', background: '#f9fafb', borderRadius: '8px'}}>
                    <div className="form-group">
                      <label className="form-label">
                        책 제목 {currentLang === bookFormData.default_language && '*'}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={bookFormData.translations[currentLang].title}
                        onChange={(e) => updateTranslation(currentLang, 'title', e.target.value)}
                        placeholder="예: The Little Prince"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        저자 {currentLang === bookFormData.default_language && '*'}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={bookFormData.translations[currentLang].author}
                        onChange={(e) => updateTranslation(currentLang, 'author', e.target.value)}
                        placeholder="예: Antoine de Saint-Exupéry"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">책 소개 (옵셔널)</label>
                      <textarea
                        className="form-input"
                        value={bookFormData.translations[currentLang].description}
                        onChange={(e) => updateTranslation(currentLang, 'description', e.target.value)}
                        rows="3"
                        placeholder="책에 대한 간단한 소개"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">줄거리 (옵셔널)</label>
                      <textarea
                        className="form-input"
                        value={bookFormData.translations[currentLang].summary}
                        onChange={(e) => updateTranslation(currentLang, 'summary', e.target.value)}
                        rows="8"
                        placeholder="책의 줄거리를 자세히 입력 (문항 생성에 사용됨)"
                      />
                      <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                        💡 현재: {(bookFormData.translations[currentLang].summary || '').length}자 (권장: 1,000자 이상)
                      </small>
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">원문 링크 (옵셔널)</label>
                  <input
                    type="url"
                    className="form-input"
                    value={bookFormData.original_link}
                    onChange={(e) => setBookFormData({...bookFormData, original_link: e.target.value})}
                    placeholder="https://example.com/book-original"
                  />
                  <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                    책의 원문을 볼 수 있는 링크 (있는 경우)
                  </small>
                </div>
                
                <div className="form-group">
                  <label className="form-label">동영상 링크 (옵셔널)</label>
                  {bookFormData.video_links.map((link, index) => (
                    <div key={index} style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                      <input
                        type="url"
                        className="form-input"
                        value={link}
                        onChange={(e) => updateVideoLink(index, e.target.value)}
                        placeholder={`https://youtube.com/watch?v=... (동영상 ${index + 1})`}
                        style={{flex: 1}}
                      />
                      {bookFormData.video_links.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeVideoLink(index)}
                          style={{width: 'auto', padding: '0.5rem 1rem'}}
                          title="삭제"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={addVideoLink}
                    style={{marginTop: '0.5rem', width: 'auto'}}
                  >
                    ➕ 동영상 링크 추가
                  </button>
                  <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                    책과 관련된 동영상 링크 (예: 독서 토론, 해설 영상 등)
                  </small>
                </div>

                <div className="modal-actions">
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBookForm}
                  >
                    취소
                  </button>
                  <button 
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingBook ? '💾 수정 완료' : '➕ 등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage; 